import { useCallback, useEffect, useMemo } from 'react';

import { useNavbarConfiguration, useResetUserNavbarPreferences, useUpdateUserNavbarPreferences } from '../../../queries/navigation';
import { NavbarConfigurationSource } from '../../../schemas/navigation';
import { normalizeTwoColumnLayout } from '../../../utils/normalize-two-column-layout';
import { getDefaultMainNavItemIds, getDefaultMoreMenuItemIds, SIDEBAR_API_TO_ITEM_ID, SIDEBAR_ITEM_TO_API_ID, SidebarItemId } from '../config/sidebar-items';

type SidebarLayout = {
	mainNavItems: SidebarItemId[];
	moreMenuItems: SidebarItemId[];
};

type UseSidebarPreferencesOptions = {
	enabled?: boolean;
};

const SIDEBAR_LAYOUT_CACHE_KEY = 'user-navbar-layout-cache-v1';

const readCachedSidebarLayout = (): SidebarLayout | null => {
	try {
		const raw = window.localStorage.getItem(SIDEBAR_LAYOUT_CACHE_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as { mainNavItems?: SidebarItemId[]; moreMenuItems?: SidebarItemId[] };
		if (!Array.isArray(parsed.mainNavItems) || !Array.isArray(parsed.moreMenuItems)) {
			return null;
		}

		return {
			mainNavItems: parsed.mainNavItems,
			moreMenuItems: parsed.moreMenuItems,
		};
	} catch {
		return null;
	}
};

const writeCachedSidebarLayout = (layout: SidebarLayout) => {
	try {
		window.localStorage.setItem(SIDEBAR_LAYOUT_CACHE_KEY, JSON.stringify(layout));
	} catch {
		// Ignore storage failures (private mode, quota, etc).
	}
};

const toSidebarItemIds = (itemIds: readonly string[]): SidebarItemId[] => {
	return itemIds
		.map((itemId) => SIDEBAR_API_TO_ITEM_ID[itemId as keyof typeof SIDEBAR_API_TO_ITEM_ID])
		.filter((itemId): itemId is SidebarItemId => Boolean(itemId));
};

export const useSidebarPreferences = ({ enabled = true }: UseSidebarPreferencesOptions = {}) => {
	const { data, isLoading, error } = useNavbarConfiguration(enabled);
	const updatePreferences = useUpdateUserNavbarPreferences();
	const resetPreferencesMutation = useResetUserNavbarPreferences();
	const cachedLayout = useMemo(() => readCachedSidebarLayout(), []);

	const defaultMainNavItems = useMemo(() => getDefaultMainNavItemIds(), []);
	const defaultMoreMenuItems = useMemo(() => getDefaultMoreMenuItemIds(), []);
	const defaultAvailableItemIds = useMemo(() => [...defaultMainNavItems, ...defaultMoreMenuItems], [defaultMainNavItems, defaultMoreMenuItems]);

	const availableItemIds = useMemo(() => {
		if (!data) {
			return defaultAvailableItemIds;
		}

		return toSidebarItemIds(data.availableItems);
	}, [data, defaultAvailableItemIds]);

	const layout = useMemo(() => {
		if (!data) {
			if (cachedLayout) {
				const normalizedLayout = normalizeTwoColumnLayout(cachedLayout.mainNavItems, cachedLayout.moreMenuItems, availableItemIds);
				return {
					mainNavItems: normalizedLayout.mainItems,
					moreMenuItems: normalizedLayout.moreItems,
				} satisfies SidebarLayout;
			}

			const normalizedLayout = normalizeTwoColumnLayout(defaultMainNavItems, defaultMoreMenuItems, availableItemIds);
			return {
				mainNavItems: normalizedLayout.mainItems,
				moreMenuItems: normalizedLayout.moreItems,
			} satisfies SidebarLayout;
		}

		const normalizedLayout = normalizeTwoColumnLayout(toSidebarItemIds(data.mainNavItems), toSidebarItemIds(data.moreMenuItems), availableItemIds);
		return {
			mainNavItems: normalizedLayout.mainItems,
			moreMenuItems: normalizedLayout.moreItems,
		} satisfies SidebarLayout;
	}, [availableItemIds, cachedLayout, data, defaultMainNavItems, defaultMoreMenuItems]);

	useEffect(() => {
		if (!data) {
			return;
		}

		writeCachedSidebarLayout(layout);
	}, [data, layout]);

	const savePreferences = useCallback(
		async (mainNavItems: SidebarItemId[], moreMenuItems: SidebarItemId[]) => {
			const normalizedLayout = normalizeTwoColumnLayout(mainNavItems, moreMenuItems, availableItemIds);
			await updatePreferences.mutateAsync({
				mainNavItems: normalizedLayout.mainItems.map((itemId) => SIDEBAR_ITEM_TO_API_ID[itemId]),
				moreMenuItems: normalizedLayout.moreItems.map((itemId) => SIDEBAR_ITEM_TO_API_ID[itemId]),
			});
		},
		[availableItemIds, updatePreferences]
	);

	const resetPreferences = useCallback(async () => {
		const response = await resetPreferencesMutation.mutateAsync();
		const normalizedLayout = normalizeTwoColumnLayout(toSidebarItemIds(response.mainNavItems), toSidebarItemIds(response.moreMenuItems), availableItemIds);
		return {
			mainNavItems: normalizedLayout.mainItems,
			moreMenuItems: normalizedLayout.moreItems,
		};
	}, [availableItemIds, resetPreferencesMutation]);

	return {
		mainNavItems: layout.mainNavItems,
		moreMenuItems: layout.moreMenuItems,
		availableItemIds,
		hasCachedLayout: Boolean(cachedLayout),
		source: data?.source ?? ('default' as NavbarConfigurationSource),
		isLoading: enabled ? isLoading : false,
		error: error instanceof Error ? error : null,
		savePreferences,
		resetPreferences,
		isSaving: updatePreferences.isLoading || resetPreferencesMutation.isLoading,
		isResetting: resetPreferencesMutation.isLoading,
	};
};
